import React, { useState, useMemo, useEffect } from 'react';
import { HiChevronDown, HiCollection, HiPlus, HiMinus, HiSelector, HiSearch, HiX } from 'react-icons/hi';
import { Modal } from 'react-bootstrap';
import DataTable from './DataTable';
import TrlcrlOneProject from './trlcrlOneProject';
import HeatmapCurves from './HeatmapCurves';
import ProjectConfirmModal from './ProjectConfirmModal';
import ProjectAlertModal from './ProjectAlertModal';
import AddProjectModal from './AddProjectModal';
import apiService from '../services/apiService';

const ResumoTab = ({
    data,
    onDataUpdate,
    appliedFilters,
    isModifyingProjects = false,
    setIsModifyingProjects,
    selectionMode = false,
    setSelectionMode,
    selectedRows = [],
    setSelectedRows,
    tableSearchTerm = '',
    setTableSearchTerm,
    showConfirmModal = false,
    setShowConfirmModal,
    confirmModalType = 'remove',
    setConfirmModalType,
    showAlertModal = false,
    setShowAlertModal,
    alertModalData = { type: 'success', message: '' },
    setAlertModalData,
    todosProjetosDisponiveis = [],
    projetosMap = {}
}) => {
    const [viewMode, setViewMode] = useState('DADOS');
    const [showProjetoModal, setShowProjetoModal] = useState(false);
    const [selectedProjetoData, setSelectedProjetoData] = useState(null);
    const [selectedProjetoId, setSelectedProjetoId] = useState(null);
    const [showHeatmapCurvesModal, setShowHeatmapCurvesModal] = useState(false);

    // New states for improved UI
    const [showAddProjectModal, setShowAddProjectModal] = useState(false);

    // Estado para manter lista de projetos incluídos na análise (Opção 2 - RESTful)
    const [projetosIncluidos, setProjetosIncluidos] = useState([]);

    // Inicializar projetosIncluidos quando data mudar
    useEffect(() => {
        if (data && data.Resumo && data.Resumo.dados) {
            const dadosResumo = data.Resumo.dados;

            // Com orient='split': { columns: [...], index: [...], data: [[...], [...]] }
            // Precisamos pegar os valores da coluna "index"
            if (dadosResumo.columns && dadosResumo.data) {
                const indexColumnPosition = dadosResumo.columns.indexOf("index");

                if (indexColumnPosition !== -1) {
                    const projetosIds = dadosResumo.data.map(row => row[indexColumnPosition]);
                    setProjetosIncluidos(projetosIds);
                }
            }
        }
    }, [data]);

    // Filtrar dados da tabela baseado na busca
    const filteredData = useMemo(() => {
        if (!data || !tableSearchTerm.trim()) {
            return data; // Retorna dados originais se não houver busca
        }

        // Filtrar apenas os dados da tabela (Resumo.dados)
        if (data.Resumo && data.Resumo.dados) {
            const dadosResumo = data.Resumo.dados;
            const searchLower = tableSearchTerm.toLowerCase();

            if (dadosResumo.columns && dadosResumo.data) {
                // Filtrar linhas que contenham o termo de busca em qualquer coluna
                const filteredRows = dadosResumo.data.filter(row => {
                    return row.some(cell =>
                        String(cell).toLowerCase().includes(searchLower)
                    );
                });

                // Retornar data com dados filtrados
                return {
                    ...data,
                    Resumo: {
                        ...data.Resumo,
                        dados: {
                            ...dadosResumo,
                            data: filteredRows
                        }
                    }
                };
            }
        }

        return data;
    }, [data, tableSearchTerm]);

    // Processar dados TRL/CRL para visualização
    const trlCrlData = useMemo(() => {
        if (!data || !data.Resumo || !data.Resumo.trlcrl) {
            return { scatterData: [], heatmapData: [] };
        }

        const trlcrl = data.Resumo.trlcrl;
        const scatterData = [];
        const heatmapCounts = {};

        // Processar matriz TRL x CRL
        Object.keys(trlcrl).forEach(trlKey => {
            const trlLevel = parseInt(trlKey);
            const crlValues = trlcrl[trlKey];

            Object.keys(crlValues).forEach(crlKey => {
                const crlLevel = parseInt(crlKey);
                const value = crlValues[crlKey];

                // Se há valor (não null/0), adiciona ao scatter
                if (value !== null && value !== undefined && value > 0) {
                    scatterData.push({
                        trl: trlLevel,
                        crl: crlLevel,
                        value: value
                    });

                    // Contagem para heatmap
                    const key = `${trlLevel}-${crlLevel}`;
                    heatmapCounts[key] = (heatmapCounts[key] || 0) + (typeof value === 'number' ? value : 1);
                }
            });
        });

        // Criar dados para heatmap (todos os pares TRL x CRL)
        const heatmapData = [];
        for (let trl = 1; trl <= 9; trl++) {
            for (let crl = 1; crl <= 9; crl++) {
                const key = `${trl}-${crl}`;
                heatmapData.push({
                    trl,
                    crl,
                    value: heatmapCounts[key] || 0
                });
            }
        }

        return { scatterData, heatmapData };
    }, [data]);

    // Calcular intensidade máxima para heatmap
    const maxIntensity = useMemo(() => {
        return Math.max(...trlCrlData.heatmapData.map(d => d.value), 1);
    }, [trlCrlData]);

    // Função para obter cor baseada na intensidade (colormap viridis do matplotlib)
    const getHeatmapColor = (value) => {
        // Normalizar intensidade [0, 1]
        const t = value / maxIntensity;

        // Colormap viridis do matplotlib (valores exatos)
        const viridis = [
            { pos: 0.000, r: 68, g: 1, b: 84 },      // #440154
            { pos: 0.125, r: 72, g: 40, b: 120 },    // #482878
            { pos: 0.250, r: 62, g: 73, b: 137 },    // #3e4989
            { pos: 0.375, r: 49, g: 104, b: 142 },   // #31688e
            { pos: 0.500, r: 38, g: 130, b: 142 },   // #26828e
            { pos: 0.625, r: 31, g: 158, b: 137 },   // #1f9e89
            { pos: 0.750, r: 53, g: 183, b: 121 },   // #35b779
            { pos: 0.875, r: 109, g: 205, b: 89 },   // #6dcd59
            { pos: 1.000, r: 253, g: 231, b: 37 }    // #fde725
        ];

        // Interpolação linear entre cores
        let color1, color2, localT;
        for (let i = 0; i < viridis.length - 1; i++) {
            if (t >= viridis[i].pos && t <= viridis[i + 1].pos) {
                color1 = viridis[i];
                color2 = viridis[i + 1];
                localT = (t - color1.pos) / (color2.pos - color1.pos);
                break;
            }
        }

        if (!color1) {
            color1 = viridis[viridis.length - 1];
            color2 = viridis[viridis.length - 1];
            localT = 0;
        }

        const r = Math.round(color1.r + (color2.r - color1.r) * localT);
        const g = Math.round(color1.g + (color2.g - color1.g) * localT);
        const b = Math.round(color1.b + (color2.b - color1.b) * localT);

        return `rgb(${r}, ${g}, ${b})`;
    };

    // Função para lidar com clique na linha da DataTable
    const handleRowClick = (row) => {
        let projetoId = null;
        projetoId = row["index"]
        if (projetoId && data.Resumo.trlcrlIndiv && data.Resumo.trlcrlIndiv[projetoId]) {
            setSelectedProjetoId(projetoId);
            setSelectedProjetoData(data.Resumo.trlcrlIndiv[projetoId]);
            setShowProjetoModal(true);
        } else {
            // Use inline alert for this case to avoid function order issues
            setAlertModalData({ type: 'error', message: `Dados TRL-CRL individuais não encontrados para o projeto ${projetoId || 'selecionado'}` });
            setShowAlertModal(true);
        }
    };

    // Função para lidar com mudança de seleção de projetos
    const handleSelectionChange = (newSelection) => {
        setSelectedRows(newSelection);
    };

    // Auto-dismiss alert after 5 seconds (following header.js pattern)
    useEffect(() => {
        if (showAlertModal) {
            const timer = setTimeout(() => {
                setShowAlertModal(false);
            }, 5000);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAlertModal]);

    // Helper functions for modals
    const showAlert = (message, type = 'success') => {
        setAlertModalData({ type, message });
        setShowAlertModal(true);
    };

    const showConfirm = (type) => {
        if (selectedRows.length === 0) {
            showAlert(`Selecione pelo menos um projeto para ${type === 'add' ? 'adicionar' : 'remover'}.`, 'error');
            return;
        }
        setConfirmModalType(type);
        setShowConfirmModal(true);
    };

    // Função para adicionar projetos - abre modal de seleção
    const handleAdicionarProjetos = () => {
        setShowAddProjectModal(true);
    };

    // Função para confirmar adição de projetos via AddProjectModal
    const handleConfirmAddProjects = async (projetosSelecionados) => {
        const count = projetosSelecionados.length;

        // Adicionar projetos selecionados à lista (evitar duplicatas)
        const projetosUnicos = new Set([...projetosIncluidos, ...projetosSelecionados]);
        const novosProjetosIncluidos = Array.from(projetosUnicos);

        setIsModifyingProjects(true);
        try {
            // Enviar lista completa de projetos incluídos para a API
            const response = await apiService.modificarDados(novosProjetosIncluidos, appliedFilters);

            if (response.sucesso && response.resultados) {
                // Atualizar estado local com a nova lista
                setProjetosIncluidos(novosProjetosIncluidos);

                // Chamar callback para atualizar os dados no componente pai
                if (onDataUpdate) {
                    onDataUpdate(response.resultados);
                }
                showAlert(`${count} projeto(s) adicionado(s) com sucesso!`, 'success');
            } else {
                showAlert(`Erro ao adicionar projetos: ${response.erro || 'Erro desconhecido'}`, 'error');
            }
        } catch (error) {
            console.error('Erro ao adicionar projetos:', error);
            showAlert(`Erro ao adicionar projetos: ${error.message}`, 'error');
        } finally {
            setIsModifyingProjects(false);
        }
    };

    // Função para remover projetos
    const handleRemoverProjetos = () => {
        showConfirm('remove');
    };

    // Função para confirmar a operação (chamada pelo modal)
    const handleConfirmOperation = async () => {
        const operation = confirmModalType === 'add' ? 'adicionar' : 'remover';
        const count = selectedRows.length;

        // Fechar o modal imediatamente após confirmar
        setShowConfirmModal(false);

        // Atualizar lista de projetos incluídos localmente
        let novosProjetosIncluidos;
        if (operation === 'remover') {
            // Remover projetos selecionados da lista
            novosProjetosIncluidos = projetosIncluidos.filter(p => !selectedRows.includes(p));
        } else {
            // Adicionar projetos selecionados à lista (evitar duplicatas)
            const projetosUnicos = new Set([...projetosIncluidos, ...selectedRows]);
            novosProjetosIncluidos = Array.from(projetosUnicos);
        }

        setIsModifyingProjects(true);
        try {
            // Enviar lista completa de projetos incluídos para a API
            const response = await apiService.modificarDados(novosProjetosIncluidos, appliedFilters);

            if (response.sucesso && response.resultados) {
                // Atualizar estado local com a nova lista
                setProjetosIncluidos(novosProjetosIncluidos);

                // Chamar callback para atualizar os dados no componente pai
                if (onDataUpdate) {
                    onDataUpdate(response.resultados);
                }
                // Limpar seleção
                setSelectedRows([]);

                showAlert(`${count} projeto(s) ${operation === 'adicionar' ? 'adicionado(s)' : 'removido(s)'} com sucesso!`, 'success');
            } else {
                showAlert(`Erro ao ${operation} projetos: ${response.erro || 'Erro desconhecido'}`, 'error');
            }
        } catch (error) {
            console.error(`Erro ao ${operation} projetos:`, error);
            showAlert(`Erro ao ${operation} projetos: ${error.message}`, 'error');
        } finally {
            setIsModifyingProjects(false);
        }
    };

    // Toggle selection mode
    const toggleSelectionMode = () => {
        const newSelectionMode = !selectionMode;
        setSelectionMode(newSelectionMode);

        if (!newSelectionMode) {
            // Exiting selection mode - clear selections and search
            setSelectedRows([]);
            setTableSearchTerm('');
        }
    };

    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="p-6 text-center text-gray-500">
                <p>Nenhum dado disponível</p>
            </div>
        );
    }


    const Heatmap = () => {
        if (trlCrlData.heatmapData.length === 0) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-12">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado para heatmap</p>
                    </div>
                </div>
            );
        }

        const width = 640;
        const height = 600;
        const margin = { top: 50, right: 100, bottom: 60, left: 60 };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;
        const cellSize = plotWidth / 9;

        return (
            <div className="bg-white rounded-lg" style={{ outline: 'none', border: 'none' }}>
                <div className="p-6 flex justify-center">
                    <svg
                        width={width}
                        height={height}
                        style={{ cursor: 'pointer', maxWidth: '100%', height: 'auto', outline: 'none', border: 'none', pointerEvents: 'none' }}
                        shapeRendering="crispEdges"
                        tabIndex="-1"
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        {/* Título */}
                        <text x={width / 2} y={25} textAnchor="middle" fontSize="16" fontWeight="600" fill="#1f2937">
                            {/* Heatmap TRL × CRL — {trlCrlData.scatterData.length} item(ns) */}
                            Heatmap TRL × CRL
                        </text>



                        <g transform={`translate(${margin.left}, ${margin.top})`}>
                            {/* Células do heatmap */}
                            {trlCrlData.heatmapData.map((cell, idx) => {
                                const x = (cell.crl - 1) * cellSize;
                                const y = plotHeight - cell.trl * cellSize;
                                const color = getHeatmapColor(cell.value);

                                return (
                                    <g key={`heatmap-cell-${idx}`} style={{ pointerEvents: 'bounding-box' }}>
                                        <rect
                                            x={x}
                                            y={y}
                                            width={cellSize}
                                            height={cellSize}
                                            fill={color}
                                            stroke="#3b82f6"
                                            strokeWidth="0.5"
                                            style={{ outline: 'none' }}
                                        />
                                        <title>TRL {cell.trl}, CRL {cell.crl}: {cell.value}</title>
                                    </g>
                                );
                            })}

                            {/* Eixo X (CRL) - labels */}
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                                <text
                                    key={`x-label-${i}`}
                                    x={(i - 0.5) * cellSize}
                                    y={plotHeight + 20}
                                    textAnchor="middle"
                                    fontSize="12"
                                    fill="#4b5563"
                                >
                                    {i}
                                </text>
                            ))}

                            {/* Eixo Y (TRL) - labels */}
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                                <text
                                    key={`y-label-${i}`}
                                    x={-10}
                                    y={plotHeight - (i - 0.5) * cellSize + 5}
                                    textAnchor="end"
                                    fontSize="12"
                                    fill="#4b5563"
                                >
                                    {i}
                                </text>
                            ))}

                            {/* Label do eixo X */}
                            <text
                                x={plotWidth / 2}
                                y={plotHeight + 45}
                                textAnchor="middle"
                                fontSize="14"
                                fontWeight="500"
                                fill="#1f2937"
                            >
                                CRL
                            </text>

                            {/* Label do eixo Y */}
                            <text
                                x={-40}
                                y={plotHeight / 2}
                                textAnchor="middle"
                                fontSize="14"
                                fontWeight="500"
                                fill="#1f2937"
                                transform={`rotate(-90, -40, ${plotHeight / 2})`}
                            >
                                TRL
                            </text>

                            {/* Barra de cores (legenda) */}
                            <g transform={`translate(${plotWidth + 20}, 0)`}>
                                {/* Gradiente vertical viridis */}
                                <defs>
                                    <linearGradient id="heatmapGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                                        <stop offset="0%" style={{ stopColor: '#440154', stopOpacity: 1 }} />
                                        <stop offset="12.5%" style={{ stopColor: '#482878', stopOpacity: 1 }} />
                                        <stop offset="25%" style={{ stopColor: '#3e4989', stopOpacity: 1 }} />
                                        <stop offset="37.5%" style={{ stopColor: '#31688e', stopOpacity: 1 }} />
                                        <stop offset="50%" style={{ stopColor: '#26828e', stopOpacity: 1 }} />
                                        <stop offset="62.5%" style={{ stopColor: '#1f9e89', stopOpacity: 1 }} />
                                        <stop offset="75%" style={{ stopColor: '#35b779', stopOpacity: 1 }} />
                                        <stop offset="87.5%" style={{ stopColor: '#6dcd59', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: '#fde725', stopOpacity: 1 }} />
                                    </linearGradient>
                                </defs>
                                <rect
                                    x={0}
                                    y={0}
                                    width={20}
                                    height={plotHeight}
                                    fill="url(#heatmapGradient)"
                                    stroke="#9ca3af"
                                    strokeWidth="1"
                                />
                                {/* Labels da escala */}
                                <text x={25} y={10} fontSize="11" fill="#4b5563">1.0</text>
                                <text x={25} y={plotHeight / 2} fontSize="11" fill="#4b5563">0.5</text>
                                <text x={25} y={plotHeight} fontSize="11" fill="#4b5563">0.0</text>

                                {/* Label da legenda */}
                                <text
                                    x={35}
                                    y={plotHeight / 2}
                                    fontSize="11"
                                    fontWeight="500"
                                    fill="#1f2937"
                                    transform={`rotate(-90, 55, ${plotHeight / 2})`}
                                >
                                    Concentração de projetos normalizada
                                </text>
                            </g>
                        </g>
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 h-full overflow-auto custom-scrollbar">
            {/* Header controls */}
            <div className="mb-6 space-y-4">
                {/* Main controls row */}
                <div className="flex items-center justify-between">
                    {/* View mode selector */}
                    <div className="relative inline-block">
                        <select
                            value={viewMode}
                            onChange={(e) => setViewMode(e.target.value)}
                            className="appearance-none bg-white border border-gray-300 rounded px-4 py-2 pr-8 text-sm text-gray-700 focus:border-gray-400 min-w-40"
                        >
                            <option value="DADOS">DADOS</option>
                            <option value="TRL/CRL">TRL/CRL</option>
                        </select>
                        <HiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>

                    {/* Selection controls - only show when in DADOS view */}
                    {viewMode === 'DADOS' && (
                        <div className="flex items-center space-x-3">
                            {!selectionMode ? (
                                /* Selection mode button */
                                <button
                                    onClick={toggleSelectionMode}
                                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                                    title="Ativar modo de seleção de projetos"
                                >
                                    <HiSelector className="w-4 h-4" />
                                    <span>Selecionar Projetos</span>
                                </button>
                            ) : (
                                /* Selection mode active controls */
                                <>
                                    {/* Campo de busca na tabela */}
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <HiSearch className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="block w-64 pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Buscar projeto por INDEX"
                                            value={tableSearchTerm}
                                            onChange={(e) => setTableSearchTerm(e.target.value)}
                                        />
                                        {tableSearchTerm && (
                                            <button
                                                onClick={() => setTableSearchTerm('')}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                            >
                                                <HiX className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleAdicionarProjetos}
                                        disabled={isModifyingProjects}
                                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                                        title="Adicionar projetos à análise"
                                    >
                                        <HiPlus className="w-4 h-4" />
                                        <span>Adicionar</span>
                                    </button>

                                    <button
                                        onClick={handleRemoverProjetos}
                                        disabled={selectedRows.length === 0 || isModifyingProjects}
                                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                                        title={selectedRows.length === 0 ? "Selecione projetos para remover" : `Remover ${selectedRows.length} projeto(s)`}
                                    >
                                        <HiMinus className="w-4 h-4" />
                                        <span>Remover</span>
                                    </button>

                                    <button
                                        onClick={toggleSelectionMode}
                                        className="flex items-center space-x-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium transition-colors"
                                        title="Sair do modo de seleção"
                                    >
                                        <HiX className="w-4 h-4" />
                                        <span>Sair</span>
                                    </button>

                                    {isModifyingProjects && (
                                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            <span>Processando...</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Content based on selection */}
            {viewMode === 'DADOS' ? (
                <DataTable
                    data={filteredData}
                    onRowClick={handleRowClick}
                    selectable={selectionMode}
                    selectedRows={selectedRows}
                    onSelectionChange={handleSelectionChange}
                />
            ) : viewMode === 'TRL/CRL' ? (
                <div onClick={() => setShowHeatmapCurvesModal(true)} className="space-y-6" style={{ outline: 'none', border: 'none' }} onMouseDown={(e) => e.preventDefault()}>
                    <Heatmap />
                </div>
            ) : null}

            {/* Modal para visualização individual do projeto */}
            <TrlcrlOneProject
                show={showProjetoModal}
                onHide={() => setShowProjetoModal(false)}
                projetoData={selectedProjetoData}
                projetoId={selectedProjetoId}
                projetosMap={projetosMap}
            />

            {/* Confirmation Modal */}
            <ProjectConfirmModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmOperation}
                loading={isModifyingProjects}
                type={confirmModalType}
                projectCount={selectedRows.length}
            />

            {/* Alert Modal */}
            <ProjectAlertModal
                show={showAlertModal}
                onHide={() => setShowAlertModal(false)}
                type={alertModalData.type}
                message={alertModalData.message}
            />

            {/* Add Project Modal */}
            <AddProjectModal
                show={showAddProjectModal}
                onHide={() => setShowAddProjectModal(false)}
                todosProjetosDisponiveis={todosProjetosDisponiveis}
                projetosMap={projetosMap}
                projetosJaIncluidos={projetosIncluidos}
                onConfirm={handleConfirmAddProjects}
            />

            {/* Modal React Bootstrap para visualização das curvas do heatmap */}
            <Modal
                show={showHeatmapCurvesModal}
                onHide={() => setShowHeatmapCurvesModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex items-center">
                    <Modal.Title className="text-gray-800 font-semibold flex items-center">
                        {/* <HiTrendingUp className="w-5 h-5 text-blue-600 mr-2" /> */}
                        Curvas do Heatmap TRL × CRL
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 bg-gray-50" >
                    <HeatmapCurves
                        heatmapData={trlCrlData.heatmapData}
                        maxIntensity={maxIntensity}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ResumoTab;
